// @ts-strict-ignore
import { Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { conditionallyPrefixImagesWithFilesUrl } from '../../functions/utilities/img-url-utility';
import { BoardSettingsWithParameters } from '../../model/board-settings';
import { ClientBoard } from '../../model/client-model/client-board';
import { AnalyticsService } from '../../services/analytics.service';
import { AppConfigService } from '../../services/app-config.service';
import { BackendBoardsV2Service } from '../../services/backend/backend-boards-v2.service';
import { BackendBoardsService } from '../../services/backend/backend-boards.service';
import { BackendReductionGoalsService } from '../../services/backend/backend-reduction-goals.service';
import { ErrorType } from '../../services/error-handler.service';
import { IBoardSaveManagerService } from '../../services/store/board';
import { routerEffectsActions } from '../router/router.actions';
import { selectRouteBoardId, selectRouteOrganisationId } from '../router/router.selectors';
import { boardEffectsActions, boardFacadeActions } from './board.actions';
import { toReductionGoalRequest } from './board.helpers';
import { selectBoard } from './board.selectors';

@Injectable()
export class BoardEffects {
  loadBoardIfNotLoaded$ = createEffect(() =>
    this._actions$.pipe(
      ofType(routerEffectsActions.routeBoardIdChanged),
      concatLatestFrom(() => [this._store.select(selectBoard)]),
      mergeMap(([routeBoardIdChanged, board]) => {
        const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);
        const isBoardInSync = board?.id === routeBoardIdChanged.boardId;
        const isBoardLoaded = !!board;

        if (isBoardLoaded && isBoardInSync) {
          return of(boardEffectsActions.boardLoaded());
        } else {
          return backendBoardsV2Service.getClientBoard(routeBoardIdChanged.boardId).pipe(
            map((board) => {
              this._injector.get(AnalyticsService).setBoard(board.id, board.title);
              return boardEffectsActions.setBoard({ board: { ...(<ClientBoard>board) } });
            }),
            catchError((error) => {
              error.errorType = ErrorType.BOARD_LOAD;
              return of(boardEffectsActions.boardLoadingError({ error }));
            })
          );
        }
      })
    )
  );

  saveBoardWithUpdatedProperty$ = createEffect(() =>
    this._actions$.pipe(
      ofType(boardFacadeActions.saveBoard),
      concatLatestFrom(() => [
        this._store.select(selectBoard),
        this._store.select(selectRouteBoardId),
        this._store.select(selectRouteOrganisationId),
      ]),
      switchMap(([{ updates }, board, boardId, tenantId]) => {
        return this._boardSaveManager
          .queueBoardForSaving({
            updates,
            boardId,
            tenantId,
            board,
          })
          .pipe(
            map((board) => {
              /**
               * The backend call returns parameters in boardSettings. This parameters should not be used.
               */
              const { boardSettings, ...boardWithoutBoardSettings } = board;
              const { parameters, feedbackSettings, ...boardSettingsWithoutParameters } =
                boardSettings as BoardSettingsWithParameters;
              if (boardSettingsWithoutParameters?.pointOfEntrySettings?.header?.backgroundImage?.large) {
                boardSettingsWithoutParameters.pointOfEntrySettings.header.backgroundImage.large =
                  conditionallyPrefixImagesWithFilesUrl(
                    boardSettingsWithoutParameters.pointOfEntrySettings.header.backgroundImage.large,
                    this._injector.get(AppConfigService).baseUrlFiles
                  );
              }

              // Only include feedbackSettings if it has at least one non-empty value
              const hasFeedbackContent =
                feedbackSettings && Object.values(feedbackSettings).some((value) => value !== '');

              const updatedBoard: ClientBoard = {
                ...boardWithoutBoardSettings,
                boardSettings: {
                  ...boardSettingsWithoutParameters,
                  ...(hasFeedbackContent && { feedbackSettings }),
                },
              };

              return boardEffectsActions.boardSaveComplete({ board: updatedBoard });
            }),
            catchError((error) => {
              error.errorType = ErrorType.BOARD_SAVE;
              return of(boardEffectsActions.boardSaveError({ error }));
            })
          );
      })
    )
  );

  deleteBoard$ = createEffect(() =>
    this._actions$.pipe(
      ofType(boardFacadeActions.deleteBoard),
      exhaustMap(({ boardId, tenantId }) => {
        const requests: (Observable<void> | Observable<string>)[] = [];

        const deleteClientBoard$: Observable<void> = this._backendBoardService.deleteBoard(boardId, tenantId);

        requests.push(deleteClientBoard$);

        const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);
        const deleteBasicBoard$: Observable<string> = backendBoardsV2Service.deleteBoard(boardId);
        requests.push(deleteBasicBoard$);

        return forkJoin(requests).pipe(
          map(() => {
            return boardEffectsActions.boardDeleteComplete({ boardId, tenantId });
          }),
          catchError((error) => {
            error.errorType = ErrorType.BOARD_SAVE;
            return of(boardEffectsActions.boardSaveError({ error }));
          })
        );
      })
    )
  );

  createBoardReductionGoal$ = createEffect(() =>
    this._actions$.pipe(
      ofType(boardFacadeActions.createReductionGoal),
      concatLatestFrom(() => [this._store.select(selectRouteBoardId)]),
      switchMap(([{ boardReductionGoal }, boardId]) => {
        const reductionGoal = toReductionGoalRequest(boardReductionGoal);
        return this._backendReductionGoalsService.createReductionGoal({ boardId, reductionGoal }).pipe(
          map((boardReductionGoal) => {
            return boardEffectsActions.createReductionGoalComplete({ boardReductionGoal });
          }),
          catchError((error) => {
            error.errorType = ErrorType.REDUCTION_GOAL_SAVE;
            return of(boardEffectsActions.boardSaveError({ error }));
          })
        );
      })
    )
  );

  updateBoardReductionGoal$ = createEffect(() =>
    this._actions$.pipe(
      ofType(boardFacadeActions.updateReductionGoal),
      concatLatestFrom(() => [this._store.select(selectRouteBoardId)]),
      switchMap(([{ boardReductionGoal }, boardId]) => {
        const reductionGoal = toReductionGoalRequest(boardReductionGoal);
        const reductionGoalId = boardReductionGoal.id;
        return this._backendReductionGoalsService
          .updateReductionGoalById({ boardId, reductionGoalId, reductionGoal })
          .pipe(
            map(() => {
              const { ratified, ratifiedDate } = boardReductionGoal;
              return boardEffectsActions.updateReductionGoalComplete({
                boardReductionGoal: {
                  ...boardReductionGoal,
                  ratified: !!ratified && !!ratifiedDate,
                  ratifiedDate: !!ratified && !!ratifiedDate ? ratifiedDate : null,
                },
              });
            }),
            catchError((error) => {
              error.errorType = ErrorType.REDUCTION_GOAL_SAVE;
              return of(boardEffectsActions.boardSaveError({ error }));
            })
          );
      })
    )
  );

  deleteBoardReductionGoal$ = createEffect(() =>
    this._actions$.pipe(
      ofType(boardFacadeActions.deleteReductionGoal),
      concatLatestFrom(() => [this._store.select(selectRouteBoardId)]),
      switchMap(([{ boardReductionGoal }, boardId]) => {
        const reductionGoalId = boardReductionGoal.id;
        return this._backendReductionGoalsService.deleteReductionGoalById({ boardId, reductionGoalId }).pipe(
          map(() => {
            return boardEffectsActions.deleteReductionGoalComplete({ boardReductionGoal });
          }),
          catchError((error) => {
            error.errorType = ErrorType.REDUCTION_GOAL_SAVE;
            return of(boardEffectsActions.boardSaveError({ error }));
          })
        );
      })
    )
  );

  throwBoardLoadingError$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(boardEffectsActions.boardLoadingError),
        map(({ error }) => {
          throw error;
        })
      ),
    { dispatch: false }
  );

  throwBoardSaveError$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(boardEffectsActions.boardSaveError),
        map(({ error }) => {
          throw error;
        })
      ),
    { dispatch: false }
  );

  constructor(
    private _actions$: Actions,
    private _backendBoardService: BackendBoardsService,
    private _backendReductionGoalsService: BackendReductionGoalsService,
    private _boardSaveManager: IBoardSaveManagerService,
    private _injector: Injector,
    private _store: Store
  ) {}
}
